import { LoadingButton } from "@mui/lab";
import { Box, BoxProps, Button, Typography, styled } from "@mui/material";
import NumberOfPeopleSelector from "components/NumberOfPeopleSelector";
import StartNewBookingModal from "components/StartNewBookingModal";
import generateCartArticle from "helpers/article/generateCartArticle";
import { TIME_FORMAT } from "helpers/datetime/datetimeConstants";
import getFormattedDuration from "helpers/datetime/getFormattedDuration";
import getHighestDuration from "helpers/service/getHighestDuration";
import dontForwardProps from "helpers/styles/dontForwardProps";
import useBookingHandlers from "hooks/useBookingHandlers";
import useParamOutlet from "hooks/useParamOutlet";
import useTranslatedValue from "hooks/useTranslatedValue";
import { BookingSlot } from "pages/BookingPage/bookingPageTypes";
import { getSlotDateTime } from "pages/BookingPage/BookingSlotPicker/slotPickerHelpers";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "store";
import {
  selectCart,
  selectHasFilledHomeServiceAppointmentDetails,
  selectHomeServiceNumOfPeople,
  selectOrganization,
  selectPackageById,
  selectPackageVariantWithServicesById,
  selectPackageVariantsByPackageId,
  selectPurchasableEarliestArticleFromCart,
  selectServiceById,
  selectServiceVariantsByServiceId,
} from "store/selectors";
import { addArticleToCart } from "store/slices/cart/cartSlice";
import { buildCartMetaParams, trackEvent } from "tracking";
import { checkIsHomeServiceOrganization } from "utils/organization";

import { TYPES } from "../constants";
import OutletBookingContext from "../outletContext";
import ServiceVariantItem from "../ServiceVariantItem";
import CustomBookButton from "./CustomBookButton";
import PackageListItemPrice from "./PackageListItemPrice";
import PurchasableCardPackageDetails from "./PurchasableCardPackageDetails";
import PurchasableSelectedSlotButton from "./PurchasableSelectedSlotButton";
import PurchasableSlots from "./PurchasableSlots";
import ServiceListItemPrice from "./ServiceListItemPrice";

type PurchasableCardProps = {
  serviceId?: number;
  packageId?: number;
};

type BookingServiceEmployee = [serviceVariantId: number, employeeId: Nullable<number>];

const packageColor = "#B447E6";

const PurchasableCard = ({ serviceId, packageId }: PurchasableCardProps) => {
  const { t } = useTranslation(["outletPage", "common"]);
  const { getTranslatedValue } = useTranslatedValue();

  const dispatch = useDispatch();
  const outlet = useParamOutlet();

  const {
    selectedStaff,
    selectedPurchasable,
    selectedDate,
    setSelectedPurchasable,
    setSelectedPurchasableNumberOfPeople,
    selectedPurchasableNumberOfPeople,
    setSelectedPurchasablePackage,
    setActiveBookingModal,
  } = useContext(OutletBookingContext);

  const {
    handleBookingFirstService,
    handleBookingOtherService,
    isLoadingAvailability,
    isLoadingDraft,
  } = useBookingHandlers({
    outlet,
  });

  const [cartArticleDraft, setCartArticleDraft] = useState<Nullable<CartArticle>>(null);

  const appointmentNumberOfPeople = useSelector(selectHomeServiceNumOfPeople);

  const isSinglePersonBooking = useMemo(
    () => appointmentNumberOfPeople === 1,
    [appointmentNumberOfPeople]
  );

  useEffect(() => {
    setSelectedPurchasable(null);
    setSelectedPurchasablePackage(null);
  }, [outlet, setSelectedPurchasable, setSelectedPurchasablePackage]);

  // const organization = useSelector(selectOrganization);
  const cart = useSelector(selectCart);
  const organization = useSelector(selectOrganization);
  const pkg = useSelector(selectPackageById(packageId || 0));
  const service = useSelector(selectServiceById(serviceId || 0));
  const serviceVariants = useSelector(selectServiceVariantsByServiceId(serviceId || 0));
  const packageVariants = useSelector(selectPackageVariantsByPackageId(packageId || 0));
  const packageVariant = useSelector(
    selectPackageVariantWithServicesById(packageVariants.length ? packageVariants[0].id : 0)
  );
  const isService = !!service && !!serviceVariants.length;

  const isPackage = !!pkg && !!packageVariants.length;

  const isHomeService = checkIsHomeServiceOrganization(organization?.industry!);

  const hasFilledHomeServiceAppointmentDetails = useSelector(
    selectHasFilledHomeServiceAppointmentDetails
  );

  const serviceHasOneVariant = isService && serviceVariants.length === 1;

  const packageArticlesSelector = selectPurchasableEarliestArticleFromCart(
    isPackage ? packageVariants[0].id : 0,
    TYPES.PACKAGES
  );

  useEffect(() => {
    if (isSinglePersonBooking) {
      setSelectedPurchasableNumberOfPeople(appointmentNumberOfPeople);
    }
  }, [appointmentNumberOfPeople, setSelectedPurchasableNumberOfPeople]);

  const serviceVariantArticlesSelector = selectPurchasableEarliestArticleFromCart(
    serviceHasOneVariant ? serviceVariants[0].id : 0,
    TYPES.SERVICES
  );

  const earliestPurchasableArticleTimeInCart = useSelector(
    isPackage ? packageArticlesSelector : serviceVariantArticlesSelector
  );

  if (!isPackage && !isService) return null;

  const serviceAndVariantNamesAreIdentical =
    serviceHasOneVariant && serviceVariants[0].name === service.name;

  const shouldShowBookButton = isHomeService
    ? hasFilledHomeServiceAppointmentDetails && (serviceHasOneVariant || isPackage)
    : isPackage || serviceHasOneVariant;

  let title = "";
  let subtitle = "";

  if (isService) {
    title =
      serviceAndVariantNamesAreIdentical || !serviceHasOneVariant
        ? getTranslatedValue(service.t.name)
        : `${getTranslatedValue(service.t.name)} - ${getTranslatedValue(
            serviceVariants[0].t.name
          )}`;

    subtitle = serviceHasOneVariant
      ? getFormattedDuration(serviceVariants[0].duration)
      : `${t("common:upTo")} ${getFormattedDuration(getHighestDuration(serviceVariants))}`;
  } else if (isPackage) {
    title = getTranslatedValue(pkg.t.name);
    subtitle = getFormattedDuration(packageVariants[0].totalDuration);
  }

  const handleBookClick = (id: number) => {
    setActiveBookingModal(null);
    if (selectedPurchasable === id) {
      setSelectedPurchasable(null);
      setSelectedPurchasablePackage(null);
    } else {
      setSelectedPurchasable(id);
      if (isPackage) {
        setSelectedPurchasablePackage(id);
      }
      const type = isPackage ? TYPES.PACKAGES : TYPES.SERVICES;
      if (isHomeService && isSinglePersonBooking) {
        if (cart.articles.length === 0) {
          handleBookingFirstService(type, id);
        } else {
          handleBookingOtherService(type, id, packageVariant);
        }
      }
    }

    setSelectedPurchasableNumberOfPeople(null);
  };

  const handleSlotSelection = (selectedSlot: Nullable<BookingSlot>) => {
    if (selectedSlot) {
      //Change this later to display prompt of clearing cart

      if (packageVariant && selectedSlot) {
        const startTime = getSlotDateTime(selectedSlot);
        const serviceArticleSlotData: BookingServiceEmployee[] = packageVariant.serviceVariants.map(
          (serviceVariant) => [serviceVariant.id, null]
        );

        const cartArticle = generateCartArticle(
          startTime,
          serviceArticleSlotData,
          null,
          packageVariant.depositPercentage,
          undefined,
          packageVariant
        );

        if (cartArticle) {
          if (cart.articles.length && selectedSlot.date !== cart.date) {
            setCartArticleDraft(cartArticle);
          } else {
            dispatch(addArticleToCart(cartArticle));
            setSelectedPurchasable(null);
            setSelectedPurchasablePackage(null);
            if (organization?.id && organization?.name) {
              trackEvent(
                !!cart.articles.length ? "Item added to cart" : "Cart Initiated",
                { id: organization.id, name: organization.name },
                { ...buildCartMetaParams([cartArticle]), isResourceSelected: false }
              );
            }
          }
        }
      } else if (serviceHasOneVariant && selectedSlot) {
        const serviceVariant = serviceVariants[0];

        const startTime = getSlotDateTime(selectedSlot);
        const cartArticle = generateCartArticle(
          startTime,
          selectedStaff || [],
          null,
          service?.depositPercentage,
          serviceVariant,
          undefined
        );
        if (cartArticle) {
          if (cart.articles.length && selectedSlot.date !== cart.date)
            setCartArticleDraft(cartArticle);
          else {
            dispatch(addArticleToCart(cartArticle));
            setSelectedPurchasable(null);
            setSelectedPurchasablePackage(null);
            if (organization?.id && organization?.name) {
              trackEvent(
                !!cart.articles.length ? "Item added to cart" : "Cart Initiated",
                { id: organization.id, name: organization.name },
                {
                  ...buildCartMetaParams([cartArticle]),
                  isResourceSelected: selectedStaff ? !!selectedStaff : false,
                }
              );
            }
          }
        }
      } else {
        return;
      }
    } else {
      return;
    }
  };

  const isBookButtonExpanded = isPackage
    ? selectedPurchasable === packageVariants[0].id
    : serviceVariants.map((serviceVariant) => serviceVariant.id).includes(selectedPurchasable || 0);

  const shouldShowTimeButton =
    earliestPurchasableArticleTimeInCart && selectedDate.toISODate() === cart.date;

  const handleShakeButton = () => {
    const shakeButton = document.querySelector(".shake-button");
    if (shakeButton) {
      shakeButton.classList.add("shake");
      // Remove the shake class after the animation ends
      setTimeout(() => {
        shakeButton.classList.remove("shake");
      }, 500); // Duration of the shake animation
    }
  };

  return (
    <>
      <StartNewBookingModal
        open={!!cartArticleDraft}
        onClose={() => setCartArticleDraft(null)}
        cartArticle={cartArticleDraft}
      />

      <StyledBox
        isPackage={isPackage}
        isSelected={isBookButtonExpanded}
        onClick={
          isHomeService && !hasFilledHomeServiceAppointmentDetails ? handleShakeButton : undefined
        }
      >
        <Box padding={2} display="flex" flexDirection="column" rowGap={1}>
          <Box display="flex" justifyContent="space-between">
            <Box display="flex" flexDirection="column" rowGap={1} width="45%">
              <Typography
                variant="primary"
                color={isPackage ? packageColor : "black"}
                fontWeight="bold"
              >
                {title}
              </Typography>

              <Typography variant="secondary" fontWeight={500}>
                {t("glossary:duration", { durationText: subtitle })}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="flex-end">
              {isService ? (
                <ServiceListItemPrice variants={serviceVariants} />
              ) : (
                <PackageListItemPrice variants={packageVariants} />
              )}

              {shouldShowBookButton &&
                (shouldShowTimeButton ? (
                  <PurchasableSelectedSlotButton
                    label={earliestPurchasableArticleTimeInCart.earliestTime.toFormat(TIME_FORMAT)}
                    colorIndex={earliestPurchasableArticleTimeInCart.articleIndex}
                    onClick={() =>
                      handleBookClick(isPackage ? packageVariants[0].id : serviceVariants[0].id)
                    }
                  />
                ) : isHomeService && isBookButtonExpanded && !isSinglePersonBooking ? (
                  <Button
                    variant="text"
                    color="error"
                    onClick={() => {
                      setSelectedPurchasable(null);
                      setSelectedPurchasablePackage(null);
                      setSelectedPurchasableNumberOfPeople(null);
                    }}
                  >
                    {t("cancel")}
                  </Button>
                ) : isLoadingAvailability || isLoadingDraft ? (
                  <LoadingButton
                    size="small"
                    loading={isLoadingAvailability || isLoadingDraft}
                    fullWidth
                  >
                    {t("common:confirm")}
                  </LoadingButton>
                ) : (
                  <CustomBookButton
                    isSelected={isBookButtonExpanded}
                    variant="outlined"
                    isPackage={isPackage}
                    onClick={() => {
                      if (organization?.id && organization?.name) {
                        trackEvent(
                          isService ? "Service Selected" : "Package Selected",
                          { id: organization.id, name: organization.name },
                          { isPopular: isService ? service.popular : pkg?.popular }
                        );
                      }
                      handleBookClick(isPackage ? packageVariants[0].id : serviceVariants[0].id);
                    }}
                  >
                    <Typography
                      variant="primary"
                      color={isPackage ? packageColor : "primary"}
                      fontWeight="bold"
                    >
                      {isHomeService ? t("glossary:add") : t("glossary:book")}
                    </Typography>
                  </CustomBookButton>
                ))}
            </Box>
          </Box>

          {!isHomeService && isBookButtonExpanded && (serviceHasOneVariant || isPackage) && (
            <Box pb={2}>
              <PurchasableSlots
                duration={
                  serviceHasOneVariant
                    ? serviceVariants[0].duration
                    : isPackage
                    ? packageVariants[0].totalDuration
                    : 1
                }
                serviceVariantId={serviceHasOneVariant ? serviceVariants[0]?.id : undefined}
                packageVariantId={isPackage ? packageVariants[0]?.id : undefined}
                handleSlotSelection={handleSlotSelection}
              />
            </Box>
          )}
          {isBookButtonExpanded &&
            isHomeService &&
            (serviceHasOneVariant || isPackage) &&
            !isSinglePersonBooking && (
              <NumberOfPeopleSelector
                appointmentNumberOfPeople={appointmentNumberOfPeople || 4}
                selectedPurchasableNumberOfPeople={selectedPurchasableNumberOfPeople}
                setSelectedPurchasableNumberOfPeople={setSelectedPurchasableNumberOfPeople}
              />
            )}
        </Box>

        {isService && !serviceHasOneVariant && (
          <Box width="100%" pt={1}>
            {serviceVariants
              .filter(
                (variant) =>
                  !isHomeService ||
                  !cart.articles.some((article) => article.serviceVariant === variant.id)
              )
              .map((variant, index) => (
                <ServiceVariantItem
                  shouldShowCart={setSelectedPurchasable}
                  variant={variant}
                  key={variant.id}
                  displayDivider={index !== serviceVariants.length - 1}
                  onBookClick={handleBookClick}
                />
              ))}
          </Box>
        )}

        <PurchasableCardPackageDetails
          packageId={packageId || 0}
          serviceId={serviceId || 0}
          packageDescription={getTranslatedValue(pkg?.t.description)}
          serviceDescription={getTranslatedValue(service?.t.description)}
        />
      </StyledBox>
    </>
  );
};

export default PurchasableCard;

type CustomBoxProps = BoxProps & {
  isPackage: boolean;
  isSelected: boolean;
};

const StyledBox = styled(
  Box,
  dontForwardProps("isPackage", "isSelected")
)<CustomBoxProps>(({ theme, isPackage, isSelected }) => ({
  borderRadius: theme.spacing(1),
  border: isPackage ? "1px solid #D4A4EB" : "1px solid #D4D6E4",
  borderStyle: "solid",
  overflowX: "clip",
  boxShadow: isSelected ? "0px 2px 24px rgba(0, 0, 0, 0.18)" : "none",

  display: "flex",
  flexDirection: "column",
  maxWidth: "100%",
}));
